// In dev mode, Vite proxies /api to the backend server
// In production, VITE_API_URL should be the full API URL
const API_URL = (import.meta.env.VITE_API_URL || '/api').replace(/\/+$/, '')

interface ApiOptions extends RequestInit {
  workspaceId?: string | null
  skipAuth?: boolean
  suppressAlert?: boolean
}

// Token getter function - will be set by auth store
let tokenGetter: (() => Promise<string | null>) | null = null

export function setTokenGetter(getter: () => Promise<string | null>) {
  tokenGetter = getter
}

// Global alert + 401 handler - set by App.vue on boot.
// Signature: (type, message, status) where status is the HTTP status code.
let globalAlertHandler: ((type: string, message: string, status: number) => void) | null = null

export function setGlobalAlertHandler(handler: (type: string, message: string, status: number) => void) {
  globalAlertHandler = handler
}

// Impersonation token - set by impersonation store when an admin session is active.
// When present, it replaces Firebase JWT auth entirely for all API requests.
let impersonationToken: string | null = null

export function setImpersonationToken(token: string | null) {
  impersonationToken = token
}

class ApiClient {
  private baseUrl: string
  private currentWorkspaceId: string | null = null

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  setWorkspaceId(workspaceId: string | null) {
    this.currentWorkspaceId = workspaceId
  }

  getWorkspaceId(): string | null {
    return this.currentWorkspaceId
  }

  private async request<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
    const { workspaceId, skipAuth, suppressAlert, ...fetchOptions } = options

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    // Impersonation takes precedence over normal Firebase auth
    if (impersonationToken) {
      headers['X-Impersonation-Token'] = impersonationToken
    } else if (!skipAuth && tokenGetter) {
      const token = await tokenGetter()
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
    }

    // Use explicitly passed workspaceId, or fall back to the current one
    const effectiveWorkspaceId = workspaceId ?? this.currentWorkspaceId
    if (effectiveWorkspaceId) {
      // Legacy header name - backend expects X-Tenant-Id; will rename to X-Workspace-Id in a coordinated deploy
      headers['X-Tenant-Id'] = effectiveWorkspaceId
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...fetchOptions,
      headers,
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'An error occurred' }))
      const status = response.status

      // Fire global handler for system-level errors. Skip for skipAuth requests -
      // those don't carry an auth header so a 401 there is expected/handled locally.
      // Also skip for suppressAlert requests (e.g. background polling) that handle errors silently.
      if (!skipAuth && !suppressAlert && globalAlertHandler) {
        if (status === 429) {
          globalAlertHandler('warning', error.message || 'Too many requests. Please wait a moment.', status)
        } else if (status === 401) {
          globalAlertHandler('error', error.message || 'Your session has expired. Please sign in again.', status)
        } else if (status >= 500) {
          globalAlertHandler('error', error.message || 'A server error occurred. Please try again.', status)
        }
      }

      throw new Error(error.message || `HTTP ${status}`)
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T
    }

    return response.json()
  }

  async get<T>(endpoint: string, options?: ApiOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' })
  }

  async post<T>(endpoint: string, data: unknown, options?: ApiOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async put<T>(endpoint: string, data: unknown, options?: ApiOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async patch<T>(endpoint: string, data: unknown, options?: ApiOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async delete<T>(endpoint: string, options?: ApiOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' })
  }
}

export const api = new ApiClient(API_URL)
